import React from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useAuthStore } from '../../state/auth';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useThemeStore } from '../../state/theme';
import PasswordInput from '../common/PasswordInput';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoginInputs, loginSchema } from '../../validations/login';
import { login } from '../../hooks/mutations';

const Login: React.FC = () => {
  const form = useForm<LoginInputs>({
    resolver: zodResolver(loginSchema),
  });

  const { mutate: loginMutation, isPending: isLoggingIn } = login({
    onSuccess: (result) => {
      updateAuthState({
        token: result.token,
        name: result.user.name,
        account: result.user.account,
        accountType: result.user.accountType,
        isAuthenticated: true,
        role: result.user.role,
        permissions: result.user.permissions || [],
      });

      navigate('/', { replace: true });
    },
    onError: (error) => {
      toast.error(error.message);
      // Reset password field
      form.setValue('password', '');
    },
  });

  const navigate = useNavigate();
  const { updateAuthState } = useAuthStore();
  const { isDarkMode } = useThemeStore();

  const onSubmit: SubmitHandler<LoginInputs> = (data) => loginMutation(data);

  const { register } = form;

  return (
    <div
      className={`max-w-md w-full space-y-8 p-8 rounded-lg shadow ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}
    >
      <div>
        <h2
          className={`mt-6 text-center text-3xl font-extrabold ${
            isDarkMode ? 'text-gray-100' : 'text-gray-900'
          }`}
        >
          Sign in to your account
        </h2>
      </div>
      <form className="mt-8 space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
        <div className="space-y-4">
          <div>
            <label
              htmlFor="email"
              className={`block text-sm font-medium ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}
            >
              Email address
            </label>
            <input
              {...register('email')}
              type="email"
              autoFocus
              className={`mt-1 block w-full rounded-md px-3 py-2 shadow-sm focus:outline-none ${
                isDarkMode
                  ? 'bg-gray-700 border-gray-600 text-gray-100 focus:border-indigo-500 focus:ring-indigo-500'
                  : 'border border-gray-300 focus:border-indigo-500 focus:ring-indigo-500'
              }`}
            />
            {form.formState.errors.email && (
              <p className="mt-1 text-sm text-red-600">
                {form.formState.errors.email.message}
              </p>
            )}
          </div>

          <PasswordInput name="password" formObj={form} showPasswordOption />
        </div>

        <button
          type="submit"
          className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
            isLoggingIn
              ? `${isDarkMode ? 'bg-indigo-400' : 'bg-blue-400'} cursor-not-allowed`
              : `focus:outline-none focus:ring-2 focus:ring-offset-2 ${isDarkMode ? 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500' : 'bg-blue-600 hover:bg-blue-800 focus:ring-blue-500'}`
          }`}
          disabled={isLoggingIn}
        >
          {isLoggingIn ? 'Signing in...' : 'Sign in'}
        </button>
      </form>
    </div>
  );
};

export default Login;
